import { useState } from 'react';

import CustomModal from '@/components/CustomModal';
import { RadioButtonGroup } from '@/components/RadioButton';
import Select from '@/components/Select';
import { STATUS_OPTIONS, LOGIN_DAYS_OPTIONS } from '@/constants/constant';
import { ERoleAccess } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useFormatText } from '@/hooks/useFormatText';
import { ITeacherFilterValues } from '@/types';

const TeacherListFilterModal = ({
  setIsOpen,
  onApply,
  initialValues,
  isSchoolFilter = true,
  schoolOptions = [],
}: {
  setIsOpen: (open: boolean) => void;
  onApply: (filters: ITeacherFilterValues) => void;
  initialValues?: Partial<ITeacherFilterValues>;
  isSchoolFilter?: boolean;
  schoolOptions?: { value: string; label: string }[];
}) => {
  const { hasAccess } = useAccess();

  const { formatText } = useFormatText();
  const [status, setStatus] = useState(initialValues?.status || '');
  const [schoolId, setSchoolId] = useState<string[]>(
    initialValues?.school_id || []
  );
  const [loginDays, setLoginDays] = useState<string>(
    initialValues?.login_days ? String(initialValues.login_days) : ''
  );

  const handleClear = () => {
    setStatus('');
    setSchoolId([]);
    setLoginDays('');
    onApply({
      status: '',
      school_id: [],
      login_days: '',
    });
    setIsOpen(false);
  };

  const handleApply = () => {
    onApply({
      status,
      school_id: schoolId,
      login_days: loginDays ? Number(loginDays) : '',
    });
    setIsOpen(false);
  };

  if (!hasAccess(ERoleAccess.USER_MANAGEMENT)) return null;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      isHeader
      isFooter
      heading={formatText('common.actions.filter')}
      position="right"
      className="max-w-150"
      confirmButtonProps={{
        label: formatText('common.actions.show-results'),
        variant: 'primary',
        onClick: handleApply,
        disabled: !status && !schoolId.length && !loginDays,
      }}
      cancelButtonProps={{
        label: formatText('common.actions.clear-filters'),
        desructive: true,
        onClick: handleClear,
        className: 'text-sm underline px-0!',
      }}
    >
      <div className="flex flex-col gap-6 w-full">
        {/* Status */}
        <RadioButtonGroup
          name="status"
          label={formatText('user-management.status')}
          options={STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
          gap="gap-4"
          formatLabel={formatText}
        />
        {/* Schools (multi-select) */}
        {isSchoolFilter && (
          <div>
            <Select
              name={formatText('user-management.filters.school')}
              options={schoolOptions}
              selected={schoolId}
              onChange={(val) => setSchoolId(Array.isArray(val) ? val : [])}
              placeholder={formatText(
                'user-management.filters.school-placeholder'
              )}
              className="w-full"
              dropdownClassName="w-full left-0"
              isMulti
              isClearable
            />
          </div>
        )}
        {/* Login Activity */}
        <div>
          <Select
            name={formatText('user-management.filters.login-activity')}
            options={LOGIN_DAYS_OPTIONS}
            selected={loginDays}
            onChange={(val) =>
              setLoginDays(Array.isArray(val) ? val[0] || '' : val)
            }
            placeholder={formatText(
              'user-management.filters.login-activity-placeholder'
            )}
            className="w-full"
            dropdownClassName="w-full left-0"
            isTextFormat
            isClearable
          />
        </div>
      </div>
    </CustomModal>
  );
};

export default TeacherListFilterModal;
